import { DatePipe, NgClass } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MaintenanceApiService } from '../apiCalls/maintenance-api.service';
import { RenewalComponent } from './renewal/renewal.component';

export interface MaintenanceData {
  Components: ComponentsMaintenanceData[];
  IsComponents: boolean;
  MainUser: string;
  SerialNumber: string;
  Title: string;
}

interface ComponentsMaintenanceData {
  Description: string;
  HardwareWarrantyExpiryDate: string;
  IsMainComponent: boolean;
  ItemIds: string;
  SoftwareWarrantyExpiryDate: string;
}

@Component({
  selector: 'app-maintenance',
  imports: [RenewalComponent, MatExpansionModule, DatePipe, NgClass, MatButtonModule],
  templateUrl: './maintenance.component.html',
  styleUrl: './maintenance.component.scss',
})
export class MaintenanceComponent {
  showSuccessInfo = signal<boolean>(false);
  private readonly maintenanceApiSvc = inject(MaintenanceApiService);

  maintenanceData = this.maintenanceApiSvc.getMaintenance();

  maintenanceDataSort = computed(() => {
    return this.maintenanceData()?.sort((a, b) => {
      // Sort by IsComponents: false first, true after
      if (a.IsComponents !== b.IsComponents) return a.IsComponents ? 1 : -1;

      //  Sort by SerialNumber numerically
      const aSN = parseInt(a.SerialNumber || '', 10);
      const bSN = parseInt(b.SerialNumber || '', 10);

      // Put missing or invalid SerialNumbers at the end
      if (isNaN(aSN)) return 1;
      if (isNaN(bSN)) return -1;

      return aSN - bSN;
    });
  });

  getSortedComponents(components: ComponentsMaintenanceData[]): ComponentsMaintenanceData[] {
    // Create a copy to avoid mutating the original array
    return components.slice().sort((a, b) => {
      // Main components first
      if (a.IsMainComponent !== b.IsMainComponent) return a.IsMainComponent ? -1 : 1;

      // Then sort alphabetically by Description, fallback to empty string if undefined
      return (a.Description || '').localeCompare(b.Description || '');
    });
  }

  getDateCssClass(dateStr: string | null | undefined, type: 'Hardware' | 'Software'): string {
    if (!dateStr) return 'unknown';

    const date = new Date(dateStr);
    const diffDays = Math.floor((date.getTime() - Date.now()) / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return type === 'Software' ? 'expired-software' : 'expired-hardware';
    }

    return type === 'Software' ? 'valid-software' : '';
  }

  getValidSerialNumbers(): string[] {
    if (!this.maintenanceDataSort()) return [];
    return this.maintenanceDataSort()!
      .filter((c) => c.SerialNumber?.trim()) // remove null, undefined or empty/whitespace
      .map((c) => c.SerialNumber!); // Non-null assertion, weil filter schon geprüft hat
  }
}
